import { useState, useCallback } from 'react';
import { message } from 'antd';
import { CreativityType } from '../../../../types/banner';
import type { RoutedFormData } from '../../../../types/templates';
import { MenuSections } from '../../../../types/menu-sections';
import { useCreateCreativityBanner } from '../api/use-create-creativity-banner';
import { useInitializeBanner } from '../use-initialize-banner';
import { useUploadMedia } from '../../../media/use-upload-media';
import { getHandlerForTemplate } from '../../../../handlers';
import { useClientId } from '../../../use-client-id';
import { useBanners } from '../../../../context/banners';
import { useModal } from '../../../../context/modal';
import { getCreativityForms } from '../../../../clients/registry';
import { buildCleanConfig, resolveBannerType, uploadFormFiles } from './submit-helpers';

interface UseSubmitCreativityBannerParams {
  creativityType: CreativityType;
  onNavigate: (section: MenuSections) => void;
  validateForm: () => { isValid: boolean; errors: Record<string, string> };
}

export const useSubmitFormCreativity = ({
  creativityType,
  onNavigate,
  validateForm
}: UseSubmitCreativityBannerParams) => {
  const [isCreating, setIsCreating] = useState(false);

  const { refreshAllBanners } = useBanners();
  const { createCreativityBanner } = useCreateCreativityBanner();
  const { initializeBanner } = useInitializeBanner();
  const { uploadVideoDirect } = useUploadMedia();
  const { openAsset } = useModal();
  const clientId = useClientId();
  const clientHandler = getHandlerForTemplate(creativityType);
  const formConfig = getCreativityForms(clientId)[creativityType];

  const handleSubmitFormBanner = useCallback(
    async (formData: RoutedFormData) => {
      if (!validateForm().isValid) {
        message.error('Por favor, corrige los errores del formulario');
        return;
      }

      setIsCreating(true);

      try {
        const bannerId = await initializeBanner();

        const processedData = await uploadFormFiles(formData, uploadVideoDirect, clientId, bannerId);

        const finalBannerType = resolveBannerType(clientHandler, processedData, creativityType);

        const cleanedConfig = buildCleanConfig(clientHandler, processedData, creativityType, finalBannerType);

        const result = await createCreativityBanner(
          bannerId,
          finalBannerType,
          cleanedConfig,
          processedData.title as string,
          formConfig.templateFamily
        );

        if (result) {
          message.success('Tu asset se está creando y pronto estará listo en el dashboard', 3);
          await refreshAllBanners();
          openAsset(result.banner.bannerId);
          onNavigate('dashboard');
        } else {
          message.error('Error al crear el asset');
        }
      } catch (error) {
        console.error('Error creating asset:', error);
        message.error('Error al crear el asset');
      } finally {
        setIsCreating(false);
      }
    },
    [
      validateForm,
      creativityType,
      clientHandler,
      formConfig,
      clientId,
      initializeBanner,
      uploadVideoDirect,
      createCreativityBanner,
      refreshAllBanners,
      openAsset,
      onNavigate
    ]
  );

  return { handleSubmitFormBanner, isCreating };
};
